import { Injectable } from '@angular/core';
import { DomSanitizer, SafeUrl} from "@angular/platform-browser";
import { HttpClient, HttpResponse, HttpHeaders, HttpEventType, HttpDownloadProgressEvent } from '@angular/common/http';

import { environment } from '@app/environment';
import { MarvDateService } from '@marv/services';
import { ApiService, TokenService, LoggedUserService } from '@app/services';
import { PhotoFiltersValues } from '@app/components';
import { 
    Photo, OperationCard, OperationCustomerPlanningSetting, PhotoSearchCriteria, AnimaPhotoSearchCriteria, InstantPhotoFilterTag,
    PearlsPhotoSearchCriteria, PearlsPhotoProductCategoryStructure, PearlsPhotoProductCategory, PearlsPhotoStoreCheckQuestionStructure,
    PearlsPhotoStoreCheckQuestionCriteria 
} from '@app/models';

export class BulkDownloadPhotosResponse {
    type: 'INFO'|'SUCCESS'|'ERROR';
    message: string;
}

@Injectable({
  providedIn: 'root'
})
export class PhotoService {
    
    constructor(
        private domSanitizer: DomSanitizer,
        private httpClient: HttpClient,
        private apiService: ApiService,
        private tokenService: TokenService,
        private loggedUserService: LoggedUserService
    ) { }
    
    async loadOperationPhotos(operation: OperationCard, photoFiltersValues: PhotoFiltersValues, customerPlanningSetting?: OperationCustomerPlanningSetting | undefined): Promise<Photo[]>{
        let photos: Array<Photo> = [];
        let data: Array<any> = [];
        if(operation.type === 'loga'){
            data = await this.loadOperationPhotosLoga(operation.id, photoFiltersValues);
        }
        else if(operation.type === 'lgg'){
            data = await this.loadOperationPhotosPearls(operation.id, photoFiltersValues);
        }       
        
        if(data.length > 0){
            for(let d of data){                
                let photo = Photo.FromJson(operation, d);
                if(photo.urlPicture != undefined
                    && photoFiltersValues.filterPhoto(photo, customerPlanningSetting)){                        
                    this.addTokenToPhotoUrl(photo);                        
                    photos.push(photo);
                }
            }
        }
        
        return photos;
    }
    
    async loadOperationPhotosLoga(operationId: number, photoFiltersValues: PhotoFiltersValues): Promise<any[]>{       
     
        let data: Array<any> = [];
        let httpParams: any = {};
                
        if(photoFiltersValues.missionId){
            httpParams = {
                from: '',
                top: '',
                op: operationId,
                mission: photoFiltersValues.missionId                
            };
            
            let response = await this.apiService.get('order/mission-report-photo', httpParams);
            if(response?.data){
                data = response?.data;
            }
        }
        else{
            httpParams = {
                start: MarvDateService.ToISOString(photoFiltersValues.startDate),
                end: MarvDateService.ToISOString(photoFiltersValues.endDate)
            };        
            
            let body: any = {
                operation: operationId
            };

            if(photoFiltersValues.retailersCodes?.length > 0){
                body.retailerCodes = photoFiltersValues.retailersCodes;
            }

            if(photoFiltersValues.storesCodes?.length > 0){
                body.storeCodes = photoFiltersValues.storesCodes;
            }


            let picturesTypes = photoFiltersValues.getPictureTypes(operationId);
            if(picturesTypes.length > 0){
                body.focusOnPictureTypes = picturesTypes;
            }

            let animaPhotoFiltersValues = photoFiltersValues.animaPhotoFiltersValues.get(operationId);
            if (animaPhotoFiltersValues) {
                body.photoTagIDs = animaPhotoFiltersValues.photoTags;
                body.isaReportDataModelIDs = animaPhotoFiltersValues.isaReportQuestions;
                body.productDataModelIDs = animaPhotoFiltersValues.productQuestions;
                body.productIDs = animaPhotoFiltersValues.products;
            }

            let response = await this.apiService.post('order/mission-report-photo', body, httpParams);
            if(response?.data){
                data = response?.data;
            }
              
        }
        
        
        return data;
    }
    
    async loadOperationPhotosPearls(operationId: number, photoFiltersValues: PhotoFiltersValues): Promise<any[]>{
        
        let data: Array<any> = [];
        let httpParams: any = {
            start: MarvDateService.ToISOString(photoFiltersValues.startDate),
            end: MarvDateService.ToISOString(photoFiltersValues.endDate),
            ctxId: operationId
        };
        
        let body: any = {};
        
        if(photoFiltersValues.retailersCodes?.length > 0){
            body.retailerCodes = photoFiltersValues.retailersCodes;
        }
        
        if(photoFiltersValues.storesCodes?.length > 0){
            body.storeCodes = photoFiltersValues.storesCodes;
        }
       
        if(photoFiltersValues.missionId){
            body.scId = photoFiltersValues.missionId;
        }        
        
        let pearlsPhotoFiltersValues = photoFiltersValues.pearlsPhotoFiltersValues.get(operationId);
        if (pearlsPhotoFiltersValues) {
            body.scPhotoTags = pearlsPhotoFiltersValues.photoTags;
            body.productIDs = pearlsPhotoFiltersValues.products;
            body.productDataModelIDs = pearlsPhotoFiltersValues.productQuestions;
            body.posmIDs = pearlsPhotoFiltersValues.posms;
            body.posmDataModelIDs = pearlsPhotoFiltersValues.posmQuestions;
            body.scPhotoDM = pearlsPhotoFiltersValues.storeCheckDataModels;

            body.skipTaggedPictures = pearlsPhotoFiltersValues.skipTaggedPictures;
            body.skipProductPictures = pearlsPhotoFiltersValues.skipProductPictures;
            body.skipPosmPictures = pearlsPhotoFiltersValues.skipPosmPictures;
            body.skipStoreCheckDataPictures = pearlsPhotoFiltersValues.skipStoreCheckDataPictures;
            body.skipRangePictures = pearlsPhotoFiltersValues.skipRangePictures;
            body.skipStitchedPhotos = pearlsPhotoFiltersValues.skipStitchedPhotos;
            body.skipTaskPictures = pearlsPhotoFiltersValues.skipTaskPictures;
        }
        
        let response = await this.apiService.post('photo-gallery', body, httpParams);
        if(response?.data){
            data = response?.data;
        }
        
        return data;
    }
    
    async loadPearlsLogo(operationId: any): Promise<string|SafeUrl> {
        let logoURL: string|SafeUrl = '';
        let params = {
            ctxId: operationId
        };
        let response = await this.apiService.get('pearlsconfig/clientlogo', params);
        if(response?.data?.content){
            logoURL = this.domSanitizer.bypassSecurityTrustUrl('data:image/jpg;base64,' + response.data.content);
        }
        return logoURL;
    }
        
    private addTokenToPhotoUrl(photo: Photo) {    
        switch (photo.source) {
            case "PCHECK": case "COACHING":
                let logaToken: string = this.tokenService.buildLogaToken();
                photo.urlPicture = photo.urlPicture + "&jtkn=" + logaToken;
                photo.urlThumbnail = photo.urlThumbnail + "&jtkn=" + logaToken;
                break;
            case "ANIMA_TAG_PHOTO": case "ANIMA_ISA_PHOTO": case "ANIMA_PRODUCT_PHOTO":
                let animaToken: string = this.tokenService.buildAnimaToken(photo.logaWorkDayId, photo.lastModified);
                photo.urlPicture = photo.urlPicture + "&tkn=" + animaToken;
                photo.urlThumbnail = photo.urlThumbnail + "&tkn=" + animaToken;
                break;
          case "PEARLS":
                break;
        }
    }
    
    async downloadFile(url: string): Promise<HttpResponse<Blob>|null>{
        return new Promise((resolve) => {          
            let headers: HttpHeaders = new HttpHeaders();
            headers = headers.append("X-APP-CODE","locupa3");            
            this.httpClient.get(url, { observe: 'response', responseType: 'blob', headers: headers }).subscribe(
                (response) => {                    
                    resolve(response);
                },
                (error) => {
                    resolve(null);
                }
            );            
        });
    }
    
    getFileNameFromHeader(headers: HttpHeaders): string {
        let filename: string = '';
        let disposition = headers.get("Content-Disposition");
        if (disposition) {
            let filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
            let matches = filenameRegex.exec(disposition);
            if (matches != null && matches[1]) {
                filename = matches[1].replace(/['"]/g, '');
            }
        }
        return filename;
    }
    
    async loadInstantPhotoFilterTags(operationsId: Array<number>): Promise<InstantPhotoFilterTag[]> {
        let instantPhotoFilterTags: Array<InstantPhotoFilterTag> = [];
        let params = {
            opIDs: operationsId
        };
        let response = await this.apiService.get('photo-gallery/instant-filter-tags/', params);
        if(response?.data){
            response.data.forEach((d: any) => {
                instantPhotoFilterTags.push(InstantPhotoFilterTag.FromJson(d));
            });
            instantPhotoFilterTags.sort(((a, b): number => {
                let rA = (a.rank == null ? 99999 : a.rank);
                let rB = (b.rank == null ? 99999 : b.rank);              
                return rA - rB;
            }));
        }
        return instantPhotoFilterTags;
    }
    
    async loadAnimaSearchCriteria(operationId: number): Promise<AnimaPhotoSearchCriteria|null> {
        let data: AnimaPhotoSearchCriteria|null = null;
        let response = await this.apiService.get('photo-gallery/' + operationId + '/anima-search-criteria');
        if(response?.data){
            let result = response.data;
            data = {
                photoTags: result.photoTags,
                photoTagCriteriaChunks: PhotoFiltersValues.SplitList(result.photoTags, 4),
                isaReportDataModelGroups: result.isaReportDataModelGroups,
                productDataModels: result.productDataModels,
                productDataModelsChunks: PhotoFiltersValues.SplitList(result.productDataModels, 4),
                productCategories: result.productCategories
            };

            data.isaReportDataModelGroups.forEach(group => {
                group.isaReportDataModelChunks = PhotoFiltersValues.SplitList(group.isaReportDataModels, 4);
            })

            data.productCategories.forEach(category => {
                category.ranges.forEach(range => {
                  range.productChunks = PhotoFiltersValues.SplitList(range.products, 4);
                })
            })
        }
        return data;
        
    }
    
    async loadPearlsSearchCriteria(operationId: number): Promise<PearlsPhotoSearchCriteria|null> {
        let data: PearlsPhotoSearchCriteria|null = null;
        let response = await this.apiService.get('photo-gallery/' + operationId + '/pearls-search-criteria');
        if(response?.data){
            let result = response.data;
            let productCategories = await this.loadPearlsProductStructure(operationId);
            
            let scDmPhotoCriteriaChunks: Array<PearlsPhotoStoreCheckQuestionStructure> = [];
            result.scDmPhotoCriteria.forEach((s: PearlsPhotoStoreCheckQuestionCriteria) => {
                let groupStructureIndex = scDmPhotoCriteriaChunks.findIndex(t => t.group.id === s.scDataGroup);               
                if(groupStructureIndex === -1){
                    let group = result.scDataGroupCriteria.find((g: PhotoSearchCriteria) => g.id === s.scDataGroup);
                    let groupStructure = new PearlsPhotoStoreCheckQuestionStructure();
                    groupStructure.group = group;
                    groupStructure.storeCheckDataModels.push(s);
                    scDmPhotoCriteriaChunks.push(groupStructure);
                }
                else{
                    scDmPhotoCriteriaChunks[groupStructureIndex].storeCheckDataModels.push(s);
                }                
            });
            
            data = {
                photoTagCriteria: result.photoTagCriteria,
                photoTagCriteriaChunks: PhotoFiltersValues.SplitList(result.photoTagCriteria, 5),
                prDmPhotoCriteria: result.prDmPhotoCriteria,
                prDmPhotoCriteriaChunks: PhotoFiltersValues.SplitList(result.prDmPhotoCriteria.filter((q: PhotoSearchCriteria) => q.disabled == false), 4),
                poDmPhotoCriteria: result.poDmPhotoCriteria,
                poDmPhotoCriteriaChunks: PhotoFiltersValues.SplitList(result.poDmPhotoCriteria.filter((q: PhotoSearchCriteria) => q.disabled == false), 4),
                scDmPhotoCriteria: result.scDmPhotoCriteria,
                scDmPhotoCriteriaChunks: scDmPhotoCriteriaChunks,
                scDataGroupCriteria: result.scDataGroupCriteria,
                productCategories: productCategories
            };
        }
        return data;
    }
    
    async loadPearlsProductStructure(operationId: number): Promise<PearlsPhotoProductCategoryStructure|null> {
        let productCategoryStructure: PearlsPhotoProductCategoryStructure|null = null;
        let params = {
            ctxId: operationId
        };
        let response = await this.apiService.get('products/imagestructure', params);
        if(response?.data){
            let data: Array<PearlsPhotoProductCategory> = [];
            response.data.forEach((item: any) => {
                let productCategory: PearlsPhotoProductCategory = {
                    id: item.id,
                    code: item.code,
                    name: item.name,
                    rank: item.rank,
                    useImageRec: item.useImageRec,
                    ranges: item.ranges,
                    hasProducts: PhotoFiltersValues.IsNotEmptyCategory(item, 'products'),
                    hasPosms: PhotoFiltersValues.IsNotEmptyCategory(item, 'posms'),
                };

                productCategory.ranges.forEach(range=> {
                    range.productChunks = PhotoFiltersValues.SplitList(range.products, 4);
                    range.posmChunks = PhotoFiltersValues.SplitList(range.posms, 4);
                })

                data.push(productCategory);
            });

            productCategoryStructure = {
                productCategory: data,
                hasProducts: data.filter(c=> c.hasProducts === true).length > 0,
                hasPosms: data.filter(c=> c.hasPosms === true).length > 0
            }
        }
        return productCategoryStructure;
    }
    
    async bulkDownloadPhotos(operations: Array<OperationCard>, groupBy: string, photoFiltersValues: PhotoFiltersValues): Promise<BulkDownloadPhotosResponse> {
        
        let response: BulkDownloadPhotosResponse = {type: 'ERROR', message: 'photoGallery.bulk-download.error'};
        let from = MarvDateService.FromDate(photoFiltersValues.startDate);
        let to = MarvDateService.FromDate(photoFiltersValues.endDate);
        
        if(from !== null && to !== null){
            
            let body: any = {
                groupingType: groupBy,
                pearlsFilters: [],
                logaOperationFilters: [],
                instantFilters: photoFiltersValues.instantFiltersCleaned
            };

            operations.forEach(operation => {
                let keyOperation = operation.type+'|'+operation.id;            
                if((!photoFiltersValues.operationsIds || photoFiltersValues.operationsIds.indexOf(keyOperation) !== -1)
                    && (!photoFiltersValues.typesMissionsCodes || photoFiltersValues.typesMissionsCodes.indexOf(operation.typeOfMissionCode) !== -1)){
                    if(operation.type === 'loga'){

                        let operationFilters: any = {
                            operation: operation.id,
                            from: from,
                            to: to
                        };

                        if(photoFiltersValues.retailersCodes?.length > 0){
                            operationFilters.retailerCodes = photoFiltersValues.retailersCodes;
                        }

                        if(photoFiltersValues.storesCodes?.length > 0){
                            operationFilters.storeCodes = photoFiltersValues.storesCodes;
                        }
                        
                        let picturesTypes = photoFiltersValues.getPictureTypes(operation.id);
                        if(picturesTypes.length > 0){
                            operationFilters.focusOnPictureTypes = picturesTypes;
                        }
                        
                        let animaPhotoFiltersValues = photoFiltersValues.animaPhotoFiltersValues.get(operation.id);
                        if (animaPhotoFiltersValues) {
                            operationFilters.photoTagIDs = animaPhotoFiltersValues.photoTags;
                            operationFilters.isaReportDataModelIDs = animaPhotoFiltersValues.isaReportQuestions;
                            operationFilters.productDataModelIDs = animaPhotoFiltersValues.productQuestions;
                            operationFilters.productIDs = animaPhotoFiltersValues.products;
                        }

                        body.logaOperationFilters.push(operationFilters);
                    }
                    else if(operation.type === 'lgg'){
                        
                        let operationFilters: any = {
                            contextId: operation.id,
                            filters: {
                                from: from,
                                to: to
                            }
                        };
                        
                        if(photoFiltersValues.retailersCodes?.length > 0){
                            operationFilters.filters.retailerCodes = photoFiltersValues.retailersCodes;
                        }

                        if(photoFiltersValues.storesCodes?.length > 0){
                            operationFilters.filters.storeCodes = photoFiltersValues.storesCodes;
                        }

                        if(photoFiltersValues.missionId){
                            operationFilters.filters.scId = photoFiltersValues.missionId;
                        }  
                        
                        let pearlsPhotoFiltersValues = photoFiltersValues.pearlsPhotoFiltersValues.get(operation.id);
                        if (pearlsPhotoFiltersValues) {
                            operationFilters.filters.scPhotoTags = pearlsPhotoFiltersValues.photoTags;
                            operationFilters.filters.productIDs = pearlsPhotoFiltersValues.products;
                            operationFilters.filters.productDataModelIDs = pearlsPhotoFiltersValues.productQuestions;
                            operationFilters.filters.posmIDs = pearlsPhotoFiltersValues.posms;
                            operationFilters.filters.posmDataModelIDs = pearlsPhotoFiltersValues.posmQuestions;
                            operationFilters.filters.scPhotoDM = pearlsPhotoFiltersValues.storeCheckDataModels;

                            operationFilters.filters.skipTaggedPictures = pearlsPhotoFiltersValues.skipTaggedPictures;
                            operationFilters.filters.skipProductPictures = pearlsPhotoFiltersValues.skipProductPictures;
                            operationFilters.filters.skipPosmPictures = pearlsPhotoFiltersValues.skipPosmPictures;
                            operationFilters.filters.skipStoreCheckDataPictures = pearlsPhotoFiltersValues.skipStoreCheckDataPictures;
                            operationFilters.filters.skipRangePictures = pearlsPhotoFiltersValues.skipRangePictures;
                            operationFilters.filters.skipStitchedPhotos = pearlsPhotoFiltersValues.skipStitchedPhotos;
                            operationFilters.filters.skipTaskPictures = pearlsPhotoFiltersValues.skipTaskPictures;
                        }
                        
                        body.pearlsFilters.push(operationFilters);
                    }
                }
            });
           
            response = await this.bulkDownloadPhotosRequest(body);
        }
        
        return response;
    }
    
    async bulkDownloadPhotosRequest(body: any): Promise<BulkDownloadPhotosResponse>{
        
        return new Promise((resolve) => {
            let headers: HttpHeaders = new HttpHeaders();
            headers = headers.append('Accept', 'text/event-stream');
            headers = headers.append("Authorization", "Bearer " + this.loggedUserService.jwtToken);
            headers = headers.append("X-APP-CODE","locupa3");

            let options:any = {
                headers: headers,
                withCredentials: true,
                observe: 'events',
                responseType: 'text',
                reportProgress: true
            };

            this.httpClient.post(environment.URL_API_BACKEND + 'photo-gallery/bulk-download', body, options).subscribe(
                (event: any) => {                    
                    if (event.type === HttpEventType.Response){ 
                        let response: BulkDownloadPhotosResponse = {type: 'ERROR', message: 'photoGallery.bulk-download.error'};
                        let events = event.body.split(/\r?\n\r?\n/).filter((e: string) => e.length > 0);
                        if(events.length > 0){
                            let lastEvent = events[events.length-1];
                            let dataStrEvent = lastEvent.split(/\r?\n/)
                                .filter((line: string) => line.startsWith('data:'))
                                .map((line: string) => line.replace(/^(data:)/, ''))
                                .join('')
                                .trim();
                            try{
                                let dataEvent = JSON.parse(dataStrEvent);
                                if(dataEvent.type && dataEvent.message){
                                    response.type = dataEvent.type;
                                    response.message = dataEvent.message;
                                }     
                            }
                            catch(e){
                                resolve(response);          
                            }                       
                        }                         
                        resolve(response);  
                    }
                },
                (error) => {
                    resolve({type: 'ERROR', message: 'photoGallery.bulk-download.error'});
                }
            );
        });
    }
}

